import { type ReactNode } from 'react';
import { Chip } from '../../components/ui/Chip';

/* ── Screen 27 — Individual campaign report ── */

const CAMPAIGN = {
  name: 'Senior Backend Engineers · Sydney',
  status: 'live' as const,
  day: 14,
  owner: 'Brendan Tremblay',
  ownerInitials: 'BT',
  ownerColor: 'var(--color-brand-indigo)',
  steps: 7,
  audience: 248,
  dateRange: '13 May – 26 May 2026',
};

const METRICS = [
  { label: 'Sent',         value: '184',   sub: '74% of audience',  delta: 0,    positiveGood: true },
  { label: 'Delivered',    value: '171',   sub: '92.9% of sent',    delta: null  as null | number, positiveGood: true },
  { label: 'Open rate',    value: '61.2%', sub: '113 unique opens', delta: 12.4, positiveGood: true },
  { label: 'Click rate',   value: '14.6%', sub: '25 unique clicks', delta: 2.1,  positiveGood: true },
  { label: 'Reply rate',   value: '11.9%', sub: '22 unique replies',delta: 3.6,  positiveGood: true },
  { label: 'Bounce rate',  value: '7.1%',  sub: '13 bounced',       delta: 1.4,  positiveGood: false },
  { label: 'Unsubscribed', value: '0.5%',  sub: '1 unsubscribe',    delta: null  as null | number, positiveGood: false },
];

const CHART = {
  labels: ['May 13','May 14','May 15','May 16','May 17','May 18','May 19','May 20','May 21','May 22','May 23','May 24','May 25','May 26'],
  sends:    [0, 248, 0, 0, 184, 0, 0, 160, 0, 0, 96, 0, 0, 50],
  opens:    [0, 102, 80, 28, 110, 22, 12, 96, 48, 28, 64, 20, 12, 18],
  replies:  [0, 6, 8, 3, 12, 4, 1, 9, 5, 3, 8, 2, 1, 2],
  stepMarkers: [
    { dayIndex: 1, label: 'S1' },
    { dayIndex: 4, label: 'S2' },
    { dayIndex: 7, label: 'S3 branch' },
    { dayIndex: 10, label: 'S5' },
  ],
};

type StepType = 'email' | 'wait' | 'branch' | 'task' | 'exit';
interface PerfStep { id: string; type: StepType; name: string; description: string; sent: number; opens: number; openRate: number; replies: number; replyRate: number; }
const PERF: PerfStep[] = [
  { id: 'p1', type: 'email',  name: 'Initial outreach',  description: 'Subject: Quick intro re: {{company_name}}',         sent: 184, opens: 113, openRate: 0.612, replies: 14, replyRate: 0.076 },
  { id: 'p2', type: 'email',  name: 'Follow-up #1',      description: 'Subject: Following up, open to chat?',           sent: 160, opens: 78,  openRate: 0.487, replies: 6,  replyRate: 0.038 },
  { id: 'p3', type: 'branch', name: 'Has opened?',       description: 'Routes opens to value pitch, others to retry.',     sent: 0,   opens: 0,   openRate: 0,     replies: 0,  replyRate: 0 },
  { id: 'p4', type: 'wait',   name: 'Wait',              description: '2 business days · land Tuesday AM.',                sent: 0,   opens: 0,   openRate: 0,     replies: 0,  replyRate: 0 },
  { id: 'p5', type: 'email',  name: 'Final value pitch', description: 'Subject: One last thing about {{role_name}}',       sent: 96,  opens: 41,  openRate: 0.427, replies: 2,  replyRate: 0.021 },
  { id: 'p6', type: 'task',   name: 'LinkedIn touch',    description: 'Owner sends a manual connect note.',                sent: 0,   opens: 0,   openRate: 0,     replies: 0,  replyRate: 0 },
  { id: 'p7', type: 'exit',   name: 'End sequence',      description: '30-day cool-down before re-enrol.',                 sent: 0,   opens: 0,   openRate: 0,     replies: 0,  replyRate: 0 },
];

interface Outcome { label: string; count: number; color: string }
const OUTCOMES: Outcome[] = [
  { label: 'In sequence',           count: 96, color: 'var(--color-brand-blue)' },
  { label: 'Replied',               count: 22, color: 'var(--color-brand-indigo)' },
  { label: 'Meeting booked',        count: 7,  color: 'var(--color-brand-teal)' },
  { label: 'High intent · task queued', count: 4, color: 'var(--color-warning)' },
  { label: 'No reply · final step', count: 88, color: 'var(--color-text-subtle)' },
  { label: 'Unsubscribed / negative', count: 5, color: 'var(--color-danger)' },
  { label: 'Bounced',               count: 13, color: 'var(--color-text-muted)' },
];

const COMPARE = [
  { label: 'Open rate',         this: 61.2, org: 48.4, positiveGood: true,  unit: '%' },
  { label: 'Reply rate',        this: 11.9, org: 9.2,  positiveGood: true,  unit: '%' },
  { label: 'Meetings per 1k',   this: 41,   org: 28,   positiveGood: true,  unit: '' },
  { label: 'Bounce rate',       this: 7.1,  org: 5.6,  positiveGood: false, unit: '%' },
  { label: 'Unsubscribe rate',  this: 0.5,  org: 0.8,  positiveGood: false, unit: '%' },
];

interface Recipient { id: string; initials: string; color: string; name: string; type: string; company: string; location: string; stepReached: string; lastEvent: string; lastEventAt: string; status: 'open' | 'meeting' | 'awaiting' | 'unsubscribed' | 'failed' | 'inprogress' }
const RECIPIENTS: Recipient[] = [
  { id: 'r1', initials: 'AL', color: 'var(--color-brand-indigo)', name: 'Anika Liu',    type: 'Candidate', company: 'Atlassian',    location: 'Sydney',   stepReached: 'Step 5', lastEvent: 'Replied',       lastEventAt: '2h ago',   status: 'open' },
  { id: 'r2', initials: 'JC', color: 'var(--color-brand-blue)',   name: 'James Chen',    type: 'Candidate', company: 'Canva',         location: 'Sydney',   stepReached: 'Step 3', lastEvent: 'Awaiting reply',lastEventAt: '1 day',    status: 'awaiting' },
  { id: 'r3', initials: 'PP', color: 'var(--color-brand-teal)',   name: 'Priya Patel',  type: 'Candidate', company: 'Culture Amp',   location: 'Sydney',   stepReached: 'Step 6', lastEvent: 'Meeting booked',lastEventAt: '5h ago',   status: 'meeting' },
  { id: 'r4', initials: 'TW', color: 'var(--color-warning)',      name: 'Tom Wright',    type: 'Candidate', company: 'SafetyCulture', location: 'Sydney',   stepReached: 'Step 2', lastEvent: 'Opened',        lastEventAt: 'Yesterday', status: 'inprogress' },
  { id: 'r5', initials: 'MY', color: 'var(--color-brand-indigo)', name: 'Marcus Yu',     type: 'Candidate', company: 'Atlassian',    location: 'Sydney',   stepReached: 'Step 1', lastEvent: 'Bounced',       lastEventAt: '3 days',   status: 'failed' },
  { id: 'r6', initials: 'SH', color: 'var(--color-brand-blue)',   name: 'Sara Hassan',   type: 'Candidate', company: 'Square',        location: 'Sydney',   stepReached: 'Step 4', lastEvent: 'Awaiting reply',lastEventAt: '6h ago',   status: 'awaiting' },
];

export function CampaignReportPage() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <Breadcrumb name={CAMPAIGN.name} />
      <TitleRow />
      <MetricsRow />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', minWidth: 0 }}>
          <EngagementCard />
          <StepPerformanceCard />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', minWidth: 0 }}>
          <OutcomeBreakdownCard />
          <LinkiSummaryCard />
          <CompareCard />
        </div>
      </div>

      <RecipientsCard />
    </div>
  );
}

/* ── Header ── */

function Breadcrumb({ name }: { name: string }) {
  const items: { label: string; to: string | null }[] = [
    { label: 'Engage', to: '/dashboard' },
    { label: 'Reports', to: '/reports' },
    { label: name, to: null },
  ];
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <li key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
            {it.to ? <a href={it.to} style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>{it.label}</a>
              : <span aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '520px' }}>{it.label}</span>}
            {i < items.length - 1 && <span aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}><IconChevronRight size={12} /></span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function TitleRow() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-4xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', lineHeight: 'var(--leading-tight)' }}>
            {CAMPAIGN.name}
          </h1>
          <Chip variant={CAMPAIGN.status}>Live</Chip>
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--chip-height)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--color-navy-50)', border: '1px solid var(--color-navy-100)', color: 'var(--color-text-brand)', fontFamily: 'var(--font-mono)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)' }}>
            Day {CAMPAIGN.day}
          </span>
        </div>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          Owner {CAMPAIGN.owner} · {CAMPAIGN.steps} steps · audience {CAMPAIGN.audience} · {CAMPAIGN.dateRange}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <DropdownButton><IconCalendar size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>Last 14 days</span><IconChevronDown size={12} /></DropdownButton>
        <SecondaryButton><IconDownload size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>Export CSV</span></SecondaryButton>
        <SecondaryButton><IconWorkflow size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>View workflow</span></SecondaryButton>
      </div>
    </div>
  );
}

/* ── Metrics ── */

function MetricsRow() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 'var(--space-3)' }}>
      {METRICS.map((m) => <MetricCard key={m.label} metric={m} />)}
    </div>
  );
}
function MetricCard({ metric }: { metric: typeof METRICS[number] }) {
  const delta = metric.delta;
  const up = (delta ?? 0) >= 0;
  const good = up === metric.positiveGood;
  const color = good ? 'var(--color-success)' : 'var(--color-danger)';
  return (
    <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>{metric.label}</span>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>{metric.value}</span>
      {delta !== null ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color }}>
          {up ? <IconArrowUp size={10} /> : <IconArrowDown size={10} />}{Math.abs(delta).toFixed(1)}%
        </span>
      ) : <span style={{ height: '14px' }} />}
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{metric.sub}</span>
    </div>
  );
}

/* ── Engagement chart with step markers ── */

function EngagementCard() {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', padding: 'var(--card-padding)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
          Engagement over time
        </h2>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          {[
            { label: 'Sends',   color: 'var(--color-blue-100)' },
            { label: 'Opens',   color: 'var(--color-brand-indigo)' },
            { label: 'Replies', color: 'var(--color-brand-teal)' },
          ].map((it) => (
            <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: 'var(--radius-full)', backgroundColor: it.color }} />
              {it.label}
            </span>
          ))}
        </div>
      </div>
      <CampaignChart />
    </section>
  );
}

function CampaignChart() {
  const w = 720, h = 240;
  const pad = { top: 28, right: 16, bottom: 32, left: 36 };
  const innerW = w - pad.left - pad.right, innerH = h - pad.top - pad.bottom;
  const max = Math.max(...CHART.sends, ...CHART.opens.map((v) => v * 3));
  const step = innerW / (CHART.labels.length - 1);
  const barW = Math.max(6, step * 0.4);

  const linePath = (vals: number[], scale = 1) => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${(pad.left + i * step).toFixed(1)},${(pad.top + innerH - (v * scale / max) * innerH).toFixed(1)}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(max * t));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" style={{ display: 'block' }}>
      {ticks.map((tv, i) => {
        const y = pad.top + innerH - (tv / max) * innerH;
        return (
          <g key={i}>
            <line x1={pad.left} x2={w - pad.right} y1={y} y2={y} stroke="var(--color-border-default)" strokeWidth={1} strokeDasharray={i === ticks.length - 1 ? '0' : '2 4'} />
            <text x={pad.left - 8} y={y + 4} textAnchor="end" fontSize="10" fontFamily="var(--font-sans)" fill="var(--color-text-subtle)">{tv}</text>
          </g>
        );
      })}

      {/* Step markers */}
      {CHART.stepMarkers.map((m) => {
        const x = pad.left + m.dayIndex * step;
        return (
          <g key={m.label}>
            <line x1={x} x2={x} y1={pad.top} y2={pad.top + innerH} stroke="var(--color-brand-blue)" strokeWidth={1} strokeDasharray="4 4" opacity="0.4" />
            <rect x={x - 22} y={pad.top - 22} width="44" height="16" rx="3" fill="var(--color-blue-50)" stroke="var(--color-blue-100)" />
            <text x={x} y={pad.top - 10} textAnchor="middle" fontSize="10" fontWeight="600" fontFamily="var(--font-sans)" fill="var(--color-brand-blue)">{m.label}</text>
          </g>
        );
      })}

      {CHART.sends.map((v, i) => {
        if (v === 0) return null;
        const x = pad.left + i * step - barW / 2;
        const y = pad.top + innerH - (v / max) * innerH;
        const bh = (v / max) * innerH;
        return <rect key={i} x={x} y={y} width={barW} height={bh} fill="var(--color-blue-100)" rx="2" />;
      })}

      <path d={linePath(CHART.opens, 3)}   fill="none" stroke="var(--color-brand-indigo)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <path d={linePath(CHART.replies, 10)} fill="none" stroke="var(--color-brand-teal)"   strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

      {CHART.labels.map((l, i) => {
        if (i % 2 !== 0 && i !== CHART.labels.length - 1) return null;
        return <text key={i} x={pad.left + i * step} y={h - 10} textAnchor="middle" fontSize="10" fontFamily="var(--font-sans)" fill="var(--color-text-subtle)">{l}</text>;
      })}
    </svg>
  );
}

/* ── Step-by-step performance ── */

const TYPE_ACCENTS: Record<StepType, { bg: string; fg: string; border: string }> = {
  email:  { bg: 'var(--color-blue-50)',     fg: 'var(--color-brand-blue)',   border: 'var(--color-blue-100)' },
  wait:   { bg: 'var(--color-surface-muted)', fg: 'var(--color-text-muted)', border: 'var(--color-border-default)' },
  branch: { bg: 'var(--color-indigo-50)',   fg: 'var(--color-brand-indigo)', border: 'var(--color-indigo-100)' },
  task:   { bg: 'var(--color-warning-bg)',  fg: 'var(--color-warning)',      border: 'var(--color-warning-border)' },
  exit:   { bg: 'var(--color-navy-50)',     fg: 'var(--color-brand-navy)',   border: 'var(--color-navy-100)' },
};

function StepPerformanceCard() {
  const maxSent = Math.max(...PERF.map((p) => p.sent), 1);
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <div style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: '1px solid var(--color-border-default)' }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
          Step-by-step performance
        </h2>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Step</Th>
              <Th align="right">Sent</Th>
              <Th>Opens</Th>
              <Th>Replies</Th>
            </tr>
          </thead>
          <tbody>
            {PERF.map((p, i) => {
              const accent = TYPE_ACCENTS[p.type];
              return (
                <tr key={p.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)' }}>
                  <Td><Mono>{i + 1}</Mono></Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'var(--space-8)', height: 'var(--space-8)', borderRadius: 'var(--radius-input)', backgroundColor: accent.bg, border: `1px solid ${accent.border}`, color: accent.fg }}>
                        <StepIcon type={p.type} />
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{p.name}</span>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.description}</span>
                      </div>
                    </div>
                  </Td>
                  <Td align="right">
                    {p.sent > 0 ? <Mono>{p.sent}</Mono> : <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-subtle)', fontSize: 'var(--text-sm)' }}>-</span>}
                  </Td>
                  <Td>
                    {p.sent > 0
                      ? <InlineRateBar value={p.openRate} count={p.opens} max={maxSent} sent={p.sent} />
                      : <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-subtle)', fontSize: 'var(--text-sm)' }}>-</span>}
                  </Td>
                  <Td>
                    {p.sent > 0
                      ? <InlineRateBar value={p.replyRate} count={p.replies} max={maxSent} sent={p.sent} tint="teal" />
                      : <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-subtle)', fontSize: 'var(--text-sm)' }}>-</span>}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StepIcon({ type }: { type: StepType }) {
  const size = 14;
  switch (type) {
    case 'email': return <IconMail size={size} />;
    case 'wait': return <IconClock size={size} />;
    case 'branch': return <IconBranch size={size} />;
    case 'task': return <IconCheckSquare size={size} />;
    case 'exit': return <IconLogOut size={size} />;
  }
}

function InlineRateBar({ value, count, sent: _sent, max: _max, tint = 'blue' }: { value: number; count: number; sent: number; max: number; tint?: 'blue' | 'teal' }) {
  const color = tint === 'teal' ? 'var(--color-brand-teal)' : 'var(--color-brand-blue)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: '140px' }}>
      <div style={{ flex: 1, height: '6px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-surface-muted)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(value * 100)}%`, height: '100%', backgroundColor: color, borderRadius: 'var(--radius-full)' }} />
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', minWidth: '64px', textAlign: 'right' }}>
        {count} · {(value * 100).toFixed(1)}%
      </span>
    </div>
  );
}

/* ── Outcome breakdown ── */

function OutcomeBreakdownCard() {
  const total = OUTCOMES.reduce((s, o) => s + o.count, 0);
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', padding: 'var(--card-padding)' }}>
      <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', marginBottom: 'var(--space-3)' }}>
        Outcome breakdown
      </h2>

      {/* Stacked bar */}
      <div style={{ display: 'flex', width: '100%', height: '12px', borderRadius: 'var(--radius-full)', overflow: 'hidden', marginBottom: 'var(--space-4)' }}>
        {OUTCOMES.map((o, i) => (
          <span key={o.label} aria-label={o.label} style={{ width: `${(o.count / total) * 100}%`, backgroundColor: o.color, borderRight: i < OUTCOMES.length - 1 ? '1px solid var(--color-surface-white)' : 'none' }} />
        ))}
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {OUTCOMES.map((o) => (
          <li key={o.label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span aria-hidden="true" style={{ width: '8px', height: '8px', borderRadius: 'var(--radius-full)', backgroundColor: o.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{o.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{o.count}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', minWidth: '46px', textAlign: 'right' }}>{((o.count / total) * 100).toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LinkiSummaryCard() {
  return (
    <section style={{ border: '1px solid var(--color-linki-border)', borderRadius: 'var(--card-radius)', backgroundColor: 'var(--color-linki-bg)', overflow: 'hidden' }}>
      <header style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-linki-border)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-brand-teal)', color: 'var(--color-text-inverse)' }}>
          <IconSpark size={12} />
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-linki-text)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
          Linki summary
        </span>
      </header>
      <div style={{ padding: 'var(--space-4)', backgroundColor: 'var(--color-surface-white)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
          You&rsquo;re running 28% above org average for opens, but the value-pitch step is dragging: reply rate drops to 2.1%. Try a stronger hook in the subject line and shorten the body by ~40 words.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', height: 'var(--btn-height-sm)', padding: '0 var(--btn-padding-x-sm)', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-brand-teal)', color: 'var(--color-text-inverse)', border: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer' }}>
            <IconSpark size={10} /> Apply suggestion
          </button>
          <button type="button" style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--btn-height-sm)', padding: '0 var(--btn-padding-x-sm)', borderRadius: 'var(--btn-radius)', backgroundColor: 'transparent', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-default)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      </div>
    </section>
  );
}

function CompareCard() {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <div style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: '1px solid var(--color-border-default)' }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
          vs org average · same campaign type
        </h2>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
        <thead>
          <tr>
            <Th>Metric</Th>
            <Th align="right">This</Th>
            <Th align="right">Org avg</Th>
            <Th align="right">Δ</Th>
          </tr>
        </thead>
        <tbody>
          {COMPARE.map((c, i) => {
            const diff = c.this - c.org;
            const up = diff >= 0;
            const good = up === c.positiveGood;
            const color = good ? 'var(--color-success)' : 'var(--color-danger)';
            return (
              <tr key={c.label} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)' }}>
                <Td><span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{c.label}</span></Td>
                <Td align="right"><Mono>{c.this}{c.unit}</Mono></Td>
                <Td align="right"><Mono>{c.org}{c.unit}</Mono></Td>
                <Td align="right">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color }}>
                    {up ? <IconArrowUp size={10} /> : <IconArrowDown size={10} />}{Math.abs(diff).toFixed(1)}{c.unit}
                  </span>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/* ── Recipients ── */

function RecipientsCard() {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <header style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: '1px solid var(--color-border-default)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
          Recipients · {RECIPIENTS.length} of {CAMPAIGN.audience}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <div style={{ position: 'relative' }}>
            <span aria-hidden="true" style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-subtle)' }}><IconSearch size={14} /></span>
            <input type="search" placeholder="Filter recipients…" style={{ width: '240px', height: 'var(--input-height-sm)', paddingLeft: 'var(--space-8)', paddingRight: 'var(--space-3)', border: '1px solid var(--input-border)', borderRadius: 'var(--input-radius)', backgroundColor: 'var(--color-surface-muted)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-sans)', color: 'var(--color-text-primary)', outline: 'none' }} />
          </div>
          <DropdownButton><span>All statuses</span><IconChevronDown size={12} /></DropdownButton>
        </div>
      </header>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
          <thead>
            <tr>
              <Th>Contact</Th>
              <Th>Company</Th>
              <Th>Step reached</Th>
              <Th>Last event</Th>
              <Th>Status</Th>
              <Th align="right" style={{ width: '40px' }}>{' '}</Th>
            </tr>
          </thead>
          <tbody>
            {RECIPIENTS.map((r, i) => (
              <tr key={r.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)' }}>
                <Td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Avatar initials={r.initials} color={r.color} />
                    <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{r.name}</span>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{r.type}</span>
                    </span>
                  </span>
                </Td>
                <Td>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>{r.company}</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{r.location}</span>
                  </span>
                </Td>
                <Td><Mono>{r.stepReached}</Mono></Td>
                <Td>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>{r.lastEvent}</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{r.lastEventAt}</span>
                  </span>
                </Td>
                <Td><Chip variant={r.status === 'failed' ? 'failed' : r.status === 'inprogress' ? 'inprogress' : r.status}>{r.status === 'failed' ? 'Bounced' : r.status === 'inprogress' ? 'Opened' : r.lastEvent}</Chip></Td>
                <Td align="right">
                  <a href="#jt" aria-label="Open in JobTetris" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: 'var(--radius-input)', color: 'var(--color-text-muted)' }}>
                    <IconExternal size={12} />
                  </a>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── Shared ── */

function Th({ children, align = 'left', style }: { children: ReactNode; align?: 'left' | 'right'; style?: React.CSSProperties }) {
  return <th style={{ textAlign: align, height: 'var(--table-header-height)', padding: '0 var(--table-cell-padding-x)', backgroundColor: 'var(--table-header-bg)', color: 'var(--table-header-text)', fontSize: 'var(--table-header-size)', fontWeight: 'var(--table-header-weight)', letterSpacing: 'var(--table-header-tracking)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-border-default)', whiteSpace: 'nowrap', ...style }}>{children}</th>;
}
function Td({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return <td style={{ padding: 'var(--table-cell-padding-y) var(--table-cell-padding-x)', textAlign: align, verticalAlign: 'middle', height: 'var(--table-row-height-sm)' }}>{children}</td>;
}
function Mono({ children }: { children: ReactNode }) {
  return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>{children}</span>;
}
function Avatar({ initials, color }: { initials: string; color: string }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'var(--avatar-size-sm)', height: 'var(--avatar-size-sm)', borderRadius: 'var(--avatar-radius)', backgroundColor: color, color: 'var(--color-text-inverse)', fontFamily: 'var(--font-sans)', fontSize: 'var(--avatar-font-size-sm)', fontWeight: 'var(--font-weight-semibold)' }}>{initials}</span>;
}
function SecondaryButton({ children }: { children: ReactNode }) {
  return <button type="button" style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-secondary)', color: 'var(--color-action-secondary-text)', border: '1px solid var(--color-action-secondary-border)', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--btn-font-weight)', fontFamily: 'var(--font-sans)', cursor: 'pointer' }}>{children}</button>;
}
function DropdownButton({ children }: { children: ReactNode }) {
  return <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-secondary)', color: 'var(--color-action-secondary-text)', border: '1px solid var(--color-action-secondary-border)', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--font-weight-medium)', fontFamily: 'var(--font-sans)', cursor: 'pointer' }}>{children}</button>;
}

/* ── Icons ── */

function IconChevronRight({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>; }
function IconChevronDown({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>; }
function IconCalendar({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
function IconDownload({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>; }
function IconWorkflow({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="6" height="6" rx="1" /><rect x="15" y="3" width="6" height="6" rx="1" /><rect x="9" y="15" width="6" height="6" rx="1" /><path d="M6 9v3a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9" /></svg>; }
function IconSpark({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.39 6.96L21 11l-6.61 2.04L12 20l-2.39-6.96L3 11l6.61-2.04L12 2z" /></svg>; }
function IconArrowUp({ size = 10 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>; }
function IconArrowDown({ size = 10 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>; }
function IconSearch({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function IconExternal({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>; }
function IconMail({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>; }
function IconClock({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>; }
function IconBranch({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></svg>; }
function IconCheckSquare({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>; }
function IconLogOut({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>; }
